import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { useUserType } from "@/contexts/UserTypeContext";
import { FeedbackService } from "@/services/features/FeedbackService";

const SUBJECT_KEYS = ["featureRequest", "general", "testimonial"];

const Feedback: React.FC = () => {
  const { t } = useTranslation();
  const { userTypeState } = useUserType();
  const feedbackService = FeedbackService.getInstance();

  const [formData, setFormData] = useState({
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState<{ subject?: string; message?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const subjectOptions = SUBJECT_KEYS.map((key) => t(`help.feedback.form.subjects.${key}`));

  const validateForm = (): boolean => {
    const newErrors: { subject?: string; message?: string } = {};
    let isValid = true;

    if (!formData.subject) {
      newErrors.subject = t("help.feedback.form.errors.subjectRequired");
      isValid = false;
    }

    if (!formData.message.trim()) {
      newErrors.message = t("help.feedback.form.errors.messageRequired");
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Get the first allowed store ID or use 0 as fallback
      const storeId = userTypeState.allowedStores?.[0] || 0;

      await feedbackService.submitFeedback({
        storeId,
        subject: formData.subject,
        message: formData.message,
      });

      setSubmitStatus({
        type: "success",
        message: t("help.feedback.success"),
      });

      // Reset form
      setFormData({
        subject: "",
        message: "",
      });
    } catch {
      setSubmitStatus({
        type: "error",
        message: t("help.feedback.error"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              {t("nav.feedback")}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {t("help.feedback.form.placeholder")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {SUBJECT_KEYS.map((key) => (
                <React.Fragment key={key}>
                  •{" "}
                  {t("help.feedback.form.subjectDescription", {
                    subject: t(`help.feedback.form.subjects.${key}`),
                    description: t(`help.feedback.form.descriptions.${key}`),
                  })}
                  <br />
                </React.Fragment>
              ))}
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {submitStatus && (
            <Alert
              severity={submitStatus.type}
              sx={{ mb: 3 }}
              onClose={() => setSubmitStatus(null)}
            >
              {submitStatus.message}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 4 }}>
              <MuiAutocompleteMultiSelect
                label={t("help.feedback.form.subject")}
                options={subjectOptions}
                value={formData.subject ? [formData.subject] : []}
                onChange={(next) => {
                  const subject = next[0] || "";
                  setFormData((prev) => ({ ...prev, subject }));
                  if (errors.subject) {
                    setErrors((prev) => ({ ...prev, subject: undefined }));
                  }
                }}
                getOptionLabel={(option) => option}
                getOptionKey={(option) => option}
                error={!!errors.subject}
                helperText={errors.subject}
              />
            </Box>

            <Box sx={{ mb: 4 }}>
              <TextField
                fullWidth
                multiline
                rows={6}
                name="message"
                label={t("help.feedback.form.message")}
                value={formData.message}
                onChange={handleInputChange}
                error={!!errors.message}
                helperText={errors.message}
                variant="outlined"
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                sx={{
                  minWidth: 160,
                  height: 48,
                  borderRadius: 2,
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t("help.feedback.form.submit")
                )}
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default Feedback;
